import { useAuth } from '@montel/montelpro-auth';
import { AdminRole } from 'domain/account/enums';

interface useAuthRolesReturnType {
	isSuperAdmin: boolean;
	isMontelAccountAdmin: boolean;
	isMatchAdmin: boolean;
	isAlertsAdmin: boolean;
	isNewsAdmin: boolean;
	isEnAppSysAdmin: boolean;
	isEQAdmin: boolean;
	isMarketsAdmin: boolean;
	isSysPowerAdmin: boolean;
	isFlexInsightAdmin: boolean;
	isDataFabricatorAdmin: boolean;
	isMontelPPAAdmin: boolean;
	adminIsRestrictedToRole: (
		role: AdminRole,
		overridingRoles?: AdminRole[],
	) => boolean;
	canChangeProductAccess: (productId: string) => boolean;
}

const useAuthRoles = (): useAuthRolesReturnType => {
	const { user } = useAuth();
	const isSuperAdmin =
		user?.roles?.some((s: string) => s == AdminRole.SUPER_ADMIN) ?? false;
	const isMontelAccountAdmin =
		user?.roles?.some((s: string) => s == AdminRole.MONTEL_ACCOUNT_ADMIN) ??
		false;
	const isMatchAdmin =
		user?.roles?.some((s: string) => s == AdminRole.PRODUCT_ADMIN_MATCH) ??
		false;
	const isAlertsAdmin =
		user?.roles?.some((s: string) => s == AdminRole.PRODUCT_ADMIN_ALERTS) ??
		false;
	const isNewsAdmin =
		user?.roles?.some(
			(s: string) => s == AdminRole.PRODUCT_ADMIN_NEWS_PAGE,
		) ?? false;
	const isEnAppSysAdmin =
		user?.roles?.some(
			(s: string) => s == AdminRole.PRODUCT_ADMIN_ENAPPSYS,
		) ?? false;
	const isEQAdmin =
		user?.roles?.some((s: string) => s == AdminRole.PRODUCT_ADMIN_EQ) ??
		false;
	const isMarketsAdmin =
		user?.roles?.some(
			(s: string) => s == AdminRole.PRODUCT_ADMIN_MARKETS,
		) ?? false;
	const isSysPowerAdmin =
		user?.roles?.some(
			(s: string) => s == AdminRole.PRODUCT_ADMIN_SYSPOWER,
		) ?? false;
	const isFlexInsightAdmin =
		user?.roles?.some(
			(s: string) => s == AdminRole.PRODUCT_ADMIN_FLEXINSIGHT,
		) ?? false;
	const isDataFabricatorAdmin =
		user?.roles?.some(
			(s: string) => s == AdminRole.PRODUCT_ADMIN_DATAFABRICATOR,
		) ?? false;
	const isMontelPPAAdmin =
		user?.roles?.some(
			(s: string) => s == AdminRole.PRODUCT_ADMIN_MONTELPPA,
		) ?? false;

	const isRestrictedToRole = (
		role: AdminRole,
		overridingRoles: AdminRole[] = [
			AdminRole.SUPER_ADMIN,
			AdminRole.MONTEL_ACCOUNT_ADMIN,
		],
	) =>
		!overridingRoles.some((r) => user?.roles?.includes(r)) &&
		user?.roles?.some((s: string) => s == role);

	const canChangeProductAccess = (productId: string) => {
		switch (productId.toLowerCase()) {
			case process.env.REACT_APP_MATCH_APPLICATION_ID?.toLowerCase():
				return isSuperAdmin || isMontelAccountAdmin || isMatchAdmin;
			case process.env.REACT_APP_NEWS_PAGE_APPLICATION_ID?.toLowerCase():
				return isSuperAdmin || isMontelAccountAdmin || isNewsAdmin;
			case process.env.REACT_APP_ALERTS_APPLICATION_ID?.toLowerCase():
				return isSuperAdmin || isMontelAccountAdmin || isAlertsAdmin;
			case process.env.REACT_APP_ENAPPSYS_APPLICATION_ID?.toLowerCase():
				return isSuperAdmin || isMontelAccountAdmin || isEnAppSysAdmin;
			case process.env.REACT_APP_EQ_APPLICATION_ID?.toLowerCase():
				return isSuperAdmin || isMontelAccountAdmin || isEQAdmin;
			case process.env.REACT_APP_MARKETS_APPLICATION_ID?.toLowerCase():
				return isSuperAdmin || isMontelAccountAdmin || isMarketsAdmin;
			case process.env.REACT_APP_SYSPOWER_APPLICATION_ID?.toLowerCase():
				return isSuperAdmin || isMontelAccountAdmin || isSysPowerAdmin;
			case process.env.REACT_APP_FLEXINSIGHT_APPLICATION_ID?.toLowerCase():
				return (
					isSuperAdmin || isMontelAccountAdmin || isFlexInsightAdmin
				);
			case process.env.REACT_APP_DATA_FABRICATOR_APPLICATION_ID?.toLowerCase():
				return (
					isSuperAdmin ||
					isMontelAccountAdmin ||
					isDataFabricatorAdmin
				);
			case process.env.REACT_APP_MONTEL_PPA_APPLICATION_ID?.toLowerCase():
				return isSuperAdmin || isMontelAccountAdmin || isMontelPPAAdmin;
			default:
				return false;
		}
	};

	return {
		isSuperAdmin,
		isMontelAccountAdmin,
		isMatchAdmin: isMatchAdmin,
		isAlertsAdmin,
		isNewsAdmin,
		isEnAppSysAdmin,
		isEQAdmin,
		isMarketsAdmin,
		isSysPowerAdmin,
		isFlexInsightAdmin,
		isDataFabricatorAdmin,
		isMontelPPAAdmin,
		adminIsRestrictedToRole: isRestrictedToRole,
		canChangeProductAccess,
	};
};

export default useAuthRoles;
